
import React, { useState, useMemo } from 'react';
import { Translation, BlogPost, Lead } from '../types';
import { Calculator } from '../components/Calculator';
import { StorageService } from '../services/db';

interface Props {
  t: Translation;
  onNavigate: (page: string) => void;
  onPostClick: (id: string) => void;
  onSubmitLead: (e: React.FormEvent<HTMLFormElement>) => void;
}

const gDrive = (id: string) => `https://lh3.googleusercontent.com/d/${id}`;

export const Home: React.FC<Props> = ({ t, onNavigate, onPostClick, onSubmitLead }) => {
  const blogs = StorageService.getBlogs();
  const tags = StorageService.getTags();
  const gallery = StorageService.getGallery();
  
  const [activeTag, setActiveTag] = useState('All');
  
  const popularArticles = useMemo(() => blogs.filter(b => b.isPopular), [blogs]);
  const filteredArticles = useMemo(() => 
    activeTag === 'All' ? blogs : blogs.filter(b => b.tags.includes(activeTag)), 
    [blogs, activeTag]
  );

  // Випадковий вибір 6 зображень для секції архітектури
  const focusedImages = useMemo(() => {
    if (!gallery || gallery.length < 6) return gallery;
    return [...gallery].sort(() => 0.5 - Math.random()).slice(0, 6);
  }, [gallery]);

  return (
    <main className="overflow-x-hidden">
      {/* Quick Stats Section */}
      <section className="bg-white border-b border-stone-100 relative z-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-stone-100">
          <div className="p-10 lg:p-16 space-y-4 text-center group hover:bg-stone-50 transition-colors">
            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-emerald-500">Speed</span>
            <h3 className="text-3xl lg:text-4xl font-light uppercase tracking-tight">{t.hero.bullet1}</h3>
            <p className="text-xs text-stone-400 uppercase font-bold tracking-widest">Rapid Deployment</p>
          </div>
          <div className="p-10 lg:p-16 space-y-4 text-center group hover:bg-stone-50 transition-colors">
            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-stone-900">Return</span>
            <h3 className="text-3xl lg:text-4xl font-light uppercase tracking-tight">{t.hero.bullet2}</h3>
            <p className="text-xs text-stone-400 uppercase font-bold tracking-widest">Investment Payback</p>
          </div>
          <div className="p-10 lg:p-16 space-y-4 text-center group hover:bg-stone-50 transition-colors">
            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-stone-900">Daily Revenue</span>
            <h3 className="text-3xl lg:text-4xl font-light uppercase tracking-tight">{t.hero.bullet3}</h3>
            <p className="text-xs text-stone-400 uppercase font-bold tracking-widest">Passive Income Stream</p>
          </div>
        </div>
      </section>

      {/* Popular Insights */}
      <section className="py-20 px-4 md:px-6 bg-stone-50 relative z-10">
        <div className="max-w-7xl mx-auto space-y-8 md:space-y-12">
          <div className="flex justify-between items-end px-2">
            <div className="space-y-1">
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-stone-400">Curated Content</p>
              <h2 className="text-3xl md:text-4xl font-light uppercase tracking-tight">Popular Insights</h2>
            </div>
            <button onClick={() => onNavigate('blog')} className="text-[10px] font-bold uppercase tracking-widest border-b border-stone-900 pb-1">All</button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-8">
            {popularArticles.slice(0, 4).map((post, idx) => (
              <article 
                key={post.id} 
                className={`group cursor-pointer relative overflow-hidden rounded-2xl md:rounded-3xl shadow-sm hover:shadow-xl transition-all ${idx === 3 ? 'hidden md:block' : ''}`}
                onClick={() => onPostClick(post.id)}
              >
                <div className="aspect-square md:aspect-[4/5] relative">
                  <img src={post.images[0]} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" alt={post.title} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3 md:bottom-6 md:left-6 md:right-6 text-white space-y-1">
                    <span className="text-[8px] md:text-[10px] font-bold uppercase tracking-widest bg-white/20 backdrop-blur-md px-2 py-0.5 rounded-full">{post.tags[0]}</span>
                    <h3 className="text-xs md:text-xl font-bold leading-tight line-clamp-2">{post.title}</h3>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Architecture in Focus - Using Dynamic Randomized Images */}
      <section className="py-20 px-4 md:px-6 bg-white relative z-10 overflow-hidden">
        <div className="max-w-7xl mx-auto space-y-8 text-center">
          <div className="space-y-2">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-stone-400">Architecture in Focus</p>
            <h2 className="text-3xl lg:text-6xl font-light uppercase tracking-tight">Stealth Design</h2>
          </div>
          
          {focusedImages && focusedImages.length >= 4 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 h-auto">
              {/* Column 1 */}
              <div className="aspect-[3/4] md:aspect-auto md:h-full">
                <img src={focusedImages[0].url} className="w-full h-full object-cover rounded-xl shadow-sm cursor-pointer hover:opacity-90 transition-opacity" onClick={() => onNavigate('gallery')} />
              </div>

              {/* Column 2 */}
              <div className="grid grid-rows-2 gap-2 md:gap-4 aspect-[3/4] md:aspect-auto md:h-full">
                <img src={focusedImages[1].url} className="w-full h-full object-cover rounded-xl shadow-sm cursor-pointer" onClick={() => onNavigate('gallery')} />
                <img src={focusedImages[2].url} className="w-full h-full object-cover rounded-xl shadow-sm cursor-pointer" onClick={() => onNavigate('gallery')} />
              </div>

              {/* Column 3 */}
              <div className="aspect-[3/4] md:aspect-auto md:h-full">
                <img src={focusedImages[3].url} className="w-full h-full object-cover rounded-xl shadow-sm cursor-pointer" onClick={() => onNavigate('gallery')} />
              </div>

              {/* Column 4 - Desktop Only Items */}
              <div className="hidden md:flex flex-col gap-4">
                 {focusedImages[4] && <img src={focusedImages[4].url} className="w-full h-1/2 object-cover rounded-xl shadow-sm cursor-pointer" onClick={() => onNavigate('gallery')} />}
                 <div className="h-1/2 bg-stone-900 rounded-xl flex flex-col items-center justify-center text-white p-4 space-y-2">
                    <h4 className="text-lg font-light leading-tight">Stealth Series</h4>
                    <button onClick={() => onNavigate('gallery')} className="bg-white text-stone-900 px-4 py-1.5 rounded-full text-[8px] font-bold uppercase">Explore All</button>
                 </div>
              </div>

              {/* Mobile Only Call-to-Gallery (to fill 2x2 grid nicely) */}
              <div className="relative md:hidden rounded-xl overflow-hidden cursor-pointer aspect-[3/4]" onClick={() => onNavigate('gallery')}>
                <img src={focusedImages[5]?.url || focusedImages[0].url} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-stone-900/60 flex items-center justify-center">
                   <span className="text-white text-[10px] font-bold uppercase tracking-widest">+ Full View</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="py-20 text-stone-400 font-bold uppercase tracking-widest">Loading focused views...</div>
          )}
        </div>
      </section>

      {/* Category Cards */}
      <section className="py-20 px-4 md:px-6 bg-stone-50 relative z-10">
        <div className="max-w-7xl mx-auto space-y-10">
          <div className="text-center space-y-3">
            <h2 className="text-2xl md:text-3xl font-light uppercase tracking-widest">Explore by Category</h2>
            <div className="flex flex-wrap justify-center gap-2">
              <button onClick={() => setActiveTag('All')} className={`px-4 py-1.5 rounded-full text-[9px] font-bold uppercase transition-all ${activeTag === 'All' ? 'bg-stone-900 text-white' : 'bg-stone-200 text-stone-500 hover:bg-stone-300'}`}>All</button>
              {tags.map(tag => (
                <button 
                  key={tag}
                  onClick={() => setActiveTag(tag)}
                  className={`px-4 py-1.5 rounded-full text-[9px] font-bold uppercase transition-all ${activeTag === tag ? 'bg-stone-900 text-white' : 'bg-stone-200 text-stone-500'}`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {filteredArticles.slice(0, 4).map(post => (
              <div key={post.id} className="bg-white rounded-2xl p-4 flex flex-col space-y-3 border border-stone-100 hover:border-stone-300 transition-colors cursor-pointer group" onClick={() => onPostClick(post.id)}>
                <div className="aspect-video overflow-hidden rounded-lg relative">
                  <img src={post.images[0]} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                  <div className="absolute top-2 left-2 flex gap-1">
                    {post.tags.slice(0, 1).map(t => (
                      <span key={t} className="text-[7px] font-black uppercase tracking-widest bg-white/90 px-2 py-0.5 rounded shadow-sm">{t}</span>
                    ))}
                  </div>
                </div>
                <div className="space-y-1">
                  <h4 className="font-bold text-[11px] uppercase leading-tight tracking-tight text-stone-900 group-hover:text-stone-600 transition-colors">{post.title}</h4>
                  <p className="text-[10px] text-stone-400 line-clamp-2 leading-relaxed">{post.excerpt}</p>
                </div>
                <div className="pt-2 flex justify-between items-center">
                   <button className="text-[8px] font-black uppercase tracking-[0.1em] border-b border-stone-900 pb-0.5">Read More</button>
                   <span className="text-[8px] text-stone-300 font-bold">{post.date}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Glampers Club */}
      <section className="py-24 px-6 bg-stone-900 text-white relative overflow-hidden z-10">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="max-w-7xl mx-auto relative z-10 grid lg:grid-cols-2 gap-20 items-center">
          <div className="space-y-10">
            <div className="space-y-4">
              <span className="text-emerald-400 text-xs font-bold uppercase tracking-[0.3em]">Exclusive Community</span>
              <h2 className="text-4xl lg:text-6xl font-light uppercase tracking-tight leading-none">
                Elite Glampers <br /> Club Czechia
              </h2>
            </div>
            <p className="text-stone-400 text-lg leading-relaxed max-w-xl">
              Don't navigate the glamping business alone. Join our network of successful owners to share experience, 
              legal secrets, and land opportunities across the Czech Republic.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="border-l-2 border-white/10 pl-6 py-2">
                <span className="block text-2xl font-bold">40+</span>
                <span className="text-[10px] text-stone-500 uppercase font-bold tracking-widest">Active Owners</span>
              </div>
              <div className="border-l-2 border-white/10 pl-6 py-2">
                <span className="block text-2xl font-bold">12</span>
                <span className="text-[10px] text-stone-500 uppercase font-bold tracking-widest">Certified Mentors</span>
              </div>
            </div>
          </div>
          <div className="bg-white/5 backdrop-blur-xl p-8 lg:p-12 rounded-[3rem] border border-white/10 space-y-8">
            <div className="space-y-2">
              <h3 className="text-2xl font-light uppercase tracking-widest">Get Involved</h3>
              <p className="text-stone-500 text-sm">Select your path in the community</p>
            </div>
            <div className="grid gap-4">
              <button 
                onClick={() => alert('Application for Glampers Club sent!')}
                className="group bg-white text-stone-900 p-6 rounded-2xl flex items-center justify-between hover:bg-stone-100 transition-all text-left"
              >
                <div>
                  <span className="block font-bold uppercase text-xs tracking-widest">Join Community</span>
                  <span className="text-[10px] opacity-60">Networking & Knowledge Sharing</span>
                </div>
                <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3"/></svg>
              </button>
            </div>
          </div>
        </div>
      </section>

      <Calculator t={t} />

      {/* CTA Footer Form */}
      <section id="contacts" className="py-24 px-6 bg-stone-900 relative z-10">
        <div className="max-w-5xl mx-auto bg-white rounded-[3rem] p-12 lg:p-20 text-stone-900 grid lg:grid-cols-2 gap-20 items-center">
          <div className="space-y-8">
            <h2 className="text-4xl lg:text-6xl font-light uppercase tracking-tighter leading-none">Ready to <br />Invest?</h2>
            <p className="text-stone-500">Contact our analysts for a detailed P&L forecast based on your specific location.</p>
          </div>
          <form onSubmit={onSubmitLead} className="space-y-6">
            <input name="name" placeholder="Name" required className="w-full bg-stone-50 p-4 rounded-xl border-none focus:ring-2 focus:ring-stone-200 outline-none transition-all" />
            <input name="email" type="email" placeholder="Email" required className="w-full bg-stone-50 p-4 rounded-xl border-none focus:ring-2 focus:ring-stone-200 outline-none transition-all" />
            <button type="submit" className="w-full bg-stone-900 text-white p-5 rounded-xl font-bold uppercase tracking-widest text-xs shadow-xl hover:bg-stone-800 transition-all">Start Consultation</button>
          </form>
        </div>
      </section>
    </main>
  );
};
